import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AppConfig } from '@/constants/residents/config';
import { logout } from '../slices/authSlice';
import { logoutAndClear } from '@/services/shared/auth/logoutAndClear';
import { performTokenRefresh } from '@/services/shared/auth/refreshTokenHelper';
import { isNetworkError } from '@/utils/common/isNetworkError';
import { getServerErrorMessage } from '@/utils/common/serverError';
import { notifyGatewayError } from '@/constants/common/gatewayErrorMessages';
import { addRedirectBreadcrumb } from '@/services/observability/redirectTelemetry';
import { MAX_RETRIES, delay, isRetriableStatus, nextRetryDelayMs, effectiveErrorStatus } from './retryPolicy';

// Error types for better error handling
export interface ApiError {
  status: number;
  data?: {
    message?: string;
    error?: string;
    details?: any;
  };
}

export interface NetworkError {
  name: string;
  message: string;
  stack?: string;
}

// Error status codes that should not show toast notifications
export const EXEMPTED_TOAST_STATUSES = [403, 401];

// Enhanced error logging function
const logApiError = (error: any, endpoint: string, method: string) => {
  console.group(`🔴 API Error - ${method.toUpperCase()} ${endpoint}`);
  
  if (error.status) {
    console.error('Status:', error.status);
    console.error('Data:', error.data);
  } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
    console.error('Network Error:', error.message);
    console.error('Possible causes: No internet, server down, CORS issues');
  } else {
    console.error('Unknown Error:', error);
  }
  
  console.groupEnd();
};

// Base query with authentication for RTK Query (admin)
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: AppConfig.API.BASE_URL,
  credentials: 'include',
  prepareHeaders: async (headers, { getState }) => {
    // Get token from secure storage
    const token = await SecureStorage.getInstance().getToken();
    // Respect pre-set Authorization header (e.g., Basic for admin v3 login)
    if (!headers.get('Authorization') && token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
  // Add timeout for requests
  // Note: Individual queries can override this (e.g., Google login uses 120 seconds)
  timeout: 30000, // 30 seconds default
});

// Enhanced base query with retry, comprehensive error handling and automatic token refresh
export const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  // Extract endpoint info for logging
  const endpoint = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : (args.method || 'GET');
  const isIdempotent = method.toUpperCase() === 'GET' || method.toUpperCase() === 'HEAD';

  let result = await baseQueryWithAuth(args, api, extraOptions);

  // Bounded auto-retry for transient statuses — idempotent requests only,
  // never mutations (spec §5, RES-114). 504 is deliberately non-retriable.
  let attempt = 0;
  while (
    result.error &&
    isIdempotent &&
    isRetriableStatus(effectiveErrorStatus(result.error)) &&
    attempt < MAX_RETRIES
  ) {
    // Consumer cancelled (unmount, unsubscribe, navigation): don't schedule
    // the sleep — a 429 retry-after can be up to 60s — and don't re-fetch.
    if (api.signal?.aborted) break;
    const retryAfterHeader = (result as any).meta?.response?.headers?.get?.('retry-after');
    const waitMs = nextRetryDelayMs(effectiveErrorStatus(result.error) as number, attempt, retryAfterHeader);
    // null = retry-after exceeds the 60s ceiling: fail now, not a multi-minute hang (spec §5).
    if (waitMs === null) break;
    console.warn(`🟡 Retrying ${method} ${endpoint} after ${waitMs}ms (status ${result.error.status}, attempt ${attempt + 1}/${MAX_RETRIES})`);
    await delay(waitMs);
    // Re-check: the abort may have arrived during the sleep.
    if (api.signal?.aborted) break;
    attempt += 1;
    result = await baseQueryWithAuth(args, api, extraOptions);
  }

  // Handle different types of errors
  if (result.error) {
    // Log the error for debugging
    logApiError(result.error, endpoint, method);

    // Effective status for classification: an unparseable 5xx body (proxy HTML
    // error page) arrives as PARSING_ERROR with the real status in
    // originalStatus — map it back so gateway/5xx handling still fires (RES-114).
    const effStatus = effectiveErrorStatus(result.error);

    // Dual-shape read (legacy `{error: string}` and unified envelope) — RES-102.
    const errorMessage = getServerErrorMessage(result.error);

    // Unrecoverable 403 errors — token is fundamentally broken, refreshing won't help
    // Legacy pre-RES-97 backend shape. Remove after backend deploy is confirmed + one release window (RES-119 follow-up).
    const FORCE_LOGOUT_ERRORS = ['Please sign in again.', 'invalid signature'];
    const shouldForceLogout = result.error.status === 403 &&
                              FORCE_LOGOUT_ERRORS.includes(errorMessage ?? '');

    if (shouldForceLogout) {
      await logoutAndClear(api.dispatch);
      return result;
    }

    // Handle token expiration (401 or 403 with jwt expired)
    // Legacy pre-RES-97 backend shape. Remove after backend deploy is confirmed + one release window (RES-119 follow-up).
    const shouldRefresh = result.error.status === 401 ||
                         (result.error.status === 403 && errorMessage === 'jwt expired');
    
    if (shouldRefresh) {
      
      try {
        
        // Attempt token refresh (uses the same helper as BaseAPI)
        const refreshed = await performTokenRefresh();
        
        if (refreshed) {
          // Retry original request with new token
          result = await baseQueryWithAuth(args, api, extraOptions);
          if (result.error) {
            logApiError(result.error, endpoint, `${method} (retry after refresh)`);
            // This branch bypasses the else-if chain below, so surface gateway
            // failures on the retried request here too (RES-102). The notifier
            // no-ops for non-gateway statuses.
            if (typeof result.error.status === 'number') {
              notifyGatewayError(result.error.status);
            }
          }
        } else {
          console.error('🔴 RTK Query: Token refresh failed, logging out');
          await logoutAndClear(api.dispatch);
          
          // Return a more user-friendly error
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: 'Session expired. Please log in again.' },
            },
          };
        }
      } catch (refreshError) {
        console.error('🔴 RTK Query: Token refresh error:', refreshError);
        await logoutAndClear(api.dispatch);
        
        return {
          error: {
            status: 'CUSTOM_ERROR',
            data: { message: 'Session expired. Please log in again.' },
          },
        };
      }
    }
    
    // Handle network errors
    else if (isNetworkError(result.error)) {
      console.error('🔴 Network error detected');
      // Network errors will be handled by the component level error handling
    }
    
    // Handle bad-gateway / gateway-timeout distinctly (RES-102). Uses the
    // effective status so an HTML-body 5xx (PARSING_ERROR → originalStatus)
    // still surfaces the gateway toast (RES-114).
    else if (
      typeof effStatus === 'number' &&
      (effStatus === 502 || effStatus === 503 || effStatus === 504)
    ) {
      console.error(`🔴 Gateway error detected: ${effStatus} on ${endpoint}`);
      notifyGatewayError(effStatus);
    }

    // Handle server errors (5xx)
    else if (typeof effStatus === 'number' && effStatus >= 500) {
      console.error('🔴 Server error detected:', effStatus);
      // Server errors will be handled by the component level error handling
    }

    // Handle client errors (4xx)
    else if (typeof effStatus === 'number' && effStatus >= 400 && effStatus < 500) {
      console.error('🔴 Client error detected:', effStatus);
      // Client errors will be handled by the component level error handling
    }
  }

  // Redirect telemetry (observability only; no-op until Sentry lands) — RES-102.
  try {
    const meta = (result as any).meta;
    if (meta?.response) {
      addRedirectBreadcrumb('rtk', meta.request?.url, meta.response.url, meta.response.redirected);
    }
  } catch {
    // Intentionally silent: telemetry must never throw or alter response flow.
  }

  return result;
};
